import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Clock, Plus, Loader2, Edit, Trash2, Shield
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Separator } from "../ui/separator";
import CopiaSeguridad from "./CopiaSeguridad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TechAdmin = () => {
  const [accounts, setAccounts] = useState([]);
  const [crmAccounts, setCrmAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [editingPassword, setEditingPassword] = useState(null);
  const [editPassword, setEditPassword] = useState("");
  const [newEquipe, setNewEquipe] = useState("equipe1");
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newCrmPassword, setNewCrmPassword] = useState("");
  const [newRole, setNewRole] = useState("secretaire");

  const fetchAccounts = useCallback(async () => {
    try {
      const [techRes, adminRes] = await Promise.all([
        axios.get(`${API}/tech/accounts`),
        axios.get(`${API}/admin/accounts`)
      ]);
      setAccounts(techRes.data);
      setCrmAccounts(adminRes.data);
    } catch { toast.error("Error al cargar cuentas"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleCreateTech = async () => {
    if (!newUsername || !newPassword) { toast.error("Usuario y contraseña obligatorios"); return; }
    try {
      await axios.post(`${API}/tech/accounts`, { username: newUsername, password: newPassword, equipe: newEquipe });
      toast.success("Cuenta creada"); setNewUsername(""); setNewPassword(""); setNewEquipe("equipe1");
      fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const updateEquipe = async (username, equipe) => {
    try {
      await axios.put(`${API}/tech/accounts/${username}`, { equipe });
      toast.success(`Equipo asignado: ${equipe}`); fetchAccounts();
    } catch { toast.error("Error"); }
  };

  const toggleActive = async (username, active) => {
    try {
      await axios.put(`${API}/tech/accounts/${username}`, { active: !active });
      toast.success(!active ? "Cuenta activada" : "Cuenta desactivada");
      fetchAccounts();
    } catch { toast.error("Error"); }
  };

  const changePassword = async (username) => {
    if (!editPassword || editPassword.length < 4) { toast.error("Mínimo 4 caracteres"); return; }
    try {
      await axios.put(`${API}/tech/accounts/${username}`, { password: editPassword });
      toast.success("Contraseña cambiada"); setEditingPassword(null); setEditPassword("");
    } catch { toast.error("Error"); }
  };

  const deleteTech = async (username) => {
    if (!window.confirm(`Supprimer le compte "${username}" ?`)) return;
    try {
      await axios.delete(`${API}/tech/accounts/${username}`);
      toast.success("Cuenta eliminada"); fetchAccounts();
    } catch { toast.error("Error"); }
  };

  const handleCreateCrm = async () => {
    if (!newName || !newEmail || !newCrmPassword) { toast.error("Tous les champs sont obligatoires"); return; }
    try {
      await axios.post(`${API}/admin/accounts`, { name: newName, email: newEmail, password: newCrmPassword, role: newRole });
      toast.success("Compte créé"); setNewName(""); setNewEmail(""); setNewCrmPassword(""); setNewRole("secretaire");
      fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const updateRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/accounts/${userId}`, { role });
      toast.success(`Rôle mis à jour: ${role}`); fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const approveAccount = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/accounts/${userId}`, { role, approved: true });
      toast.success("Compte approuvé"); fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const rejectAccount = async (userId, name) => {
    if (!window.confirm(`Rejeter et supprimer le compte "${name}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/accounts/${userId}`);
      toast.success("Compte rejeté"); fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const deleteCrm = async (userId, name) => {
    if (!window.confirm(`Supprimer le compte "${name}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/accounts/${userId}`);
      toast.success("Compte supprimé"); fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const pendingAccounts = crmAccounts.filter(a => !a.approved || a.role === "pending");
  const approvedAccounts = crmAccounts.filter(a => a.approved && a.role !== "pending");

  const roleConfig = {
    admin: { label: "Admin", color: "bg-red-100 text-red-700 border-red-200" },
    secretaire: { label: "Secrétaire", color: "bg-blue-100 text-blue-700 border-blue-200" },
    commercial: { label: "Commercial", color: "bg-amber-100 text-amber-700 border-amber-200" },
    pending: { label: "En attente", color: "bg-slate-100 text-slate-500 border-slate-200" },
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="acces-page">
      <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Gestion des Accès</h1>
      <CopiaSeguridad modo="linea" />

      {pendingAccounts.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Comptes en attente d'approbation ({pendingAccounts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingAccounts.map(acc => (
              <div key={acc.user_id} className="flex items-center justify-between p-4 rounded-xl border bg-white border-amber-200" data-testid={`pending-${acc.user_id}`}>
                <div>
                  <p className="font-semibold text-slate-800">{acc.name}</p>
                  <p className="text-xs text-slate-400">{acc.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select className="text-sm border rounded-lg px-2 py-1.5 bg-white" defaultValue="secretaire" data-testid={`role-select-${acc.user_id}`} id={`role-${acc.user_id}`}>
                    <option value="admin">Administrateur</option>
                    <option value="secretaire">Secrétaire</option>
                    <option value="commercial">Commercial</option>
                  </select>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => {
                    const role = document.getElementById(`role-${acc.user_id}`).value;
                    approveAccount(acc.user_id, role);
                  }} data-testid={`approve-${acc.user_id}`}>
                    <CheckCircle size={14} className="mr-1" /> Approuver
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => rejectAccount(acc.user_id, acc.name)} data-testid={`reject-${acc.user_id}`}>
                    <XCircle size={14} className="mr-1" /> Rejeter
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-3">Comptes CRM</h2>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Créer un compte</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 items-end flex-wrap">
              <div className="flex-1 min-w-[150px]">
                <Label>Nom</Label>
                <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Jean Dupont" data-testid="new-crm-name" />
              </div>
              <div className="flex-1 min-w-[150px]">
                <Label>Email</Label>
                <Input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@example.com" data-testid="new-crm-email" />
              </div>
              <div className="flex-1 min-w-[120px]">
                <Label>Mot de passe</Label>
                <Input type="password" value={newCrmPassword} onChange={e => setNewCrmPassword(e.target.value)} placeholder="Min. 6 car." data-testid="new-crm-password" />
              </div>
              <div className="min-w-[140px]">
                <Label>Rôle</Label>
                <select value={newRole} onChange={e => setNewRole(e.target.value)} className="w-full h-9 border rounded-md px-3 text-sm bg-white" data-testid="new-crm-role">
                  <option value="admin">Administrateur</option>
                  <option value="secretaire">Secrétaire</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>
              <Button onClick={handleCreateCrm} className="bg-blue-600 hover:bg-blue-700" data-testid="create-crm-btn">
                <Plus size={16} className="mr-1" /> Créer
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-3">
          <CardContent className="pt-4">
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : approvedAccounts.length === 0 ? (
              <p className="text-slate-400 text-center py-6">Aucun compte CRM</p>
            ) : (
              <div className="space-y-3">
                {approvedAccounts.map(acc => (
                  <div key={acc.user_id} className="flex items-center justify-between p-4 rounded-xl border bg-white border-slate-200" data-testid={`crm-account-${acc.user_id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${acc.connected ? "bg-emerald-500" : "bg-slate-300"}`} />
                      <div>
                        <p className="font-semibold text-slate-800">{acc.name}</p>
                        <p className="text-xs text-slate-400">{acc.email}</p>
                      </div>
                      {acc.is_super_admin ? (
                        <span className="text-xs px-2 py-0.5 rounded-full border font-medium bg-gradient-to-r from-red-100 to-amber-100 text-red-800 border-red-300">
                          Super Admin
                        </span>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${roleConfig[acc.role]?.color || roleConfig.admin.color}`}>
                          {roleConfig[acc.role]?.label || acc.role}
                        </span>
                      )}
                      {acc.connected && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Connecté</span>}
                    </div>
                    {!acc.is_super_admin && (
                      <div className="flex items-center gap-2">
                        <select value={acc.role} onChange={e => updateRole(acc.user_id, e.target.value)} className="text-sm border rounded-lg px-2 py-1.5 bg-white" data-testid={`change-role-${acc.user_id}`}>
                          <option value="admin">Admin</option>
                          <option value="secretaire">Secrétaire</option>
                          <option value="commercial">Commercial</option>
                        </select>
                        <Button size="sm" variant="outline" onClick={() => deleteCrm(acc.user_id, acc.name)} data-testid={`delete-crm-${acc.user_id}`}>
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Separator />

      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-3">Comptes Techniciens (PWA)</h2>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ajouter un technicien</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label>Nom d'utilisateur</Label>
                <Input value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="equipe1" data-testid="new-tech-username" />
              </div>
              <div className="flex-1">
                <Label>Mot de passe</Label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="****" data-testid="new-tech-password" />
              </div>
              <div className="min-w-[120px]">
                <Label>Équipe</Label>
                <select value={newEquipe} onChange={e => setNewEquipe(e.target.value)} className="w-full h-9 border rounded-md px-3 text-sm bg-white" data-testid="new-tech-equipe">
                  <option value="equipe1">Equipo 1</option>
                  <option value="equipe2">Equipo 2</option>
                  <option value="equipe3">Equipo 3</option>
                </select>
              </div>
              <Button onClick={handleCreateTech} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-tech-btn">
                <Plus size={16} className="mr-1" /> Créer
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-3">
          <CardContent className="pt-4">
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : accounts.length === 0 ? (
              <p className="text-slate-400 text-center py-6">Aucun compte technicien créé</p>
            ) : (
              <div className="space-y-3">
                {accounts.map(acc => (
                  <div key={acc.username} className={`flex items-center justify-between p-4 rounded-xl border ${acc.active ? "bg-white border-slate-200" : "bg-red-50 border-red-200"}`} data-testid={`tech-account-${acc.username}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${acc.active ? "bg-emerald-500" : "bg-red-500"}`} />
                      <div>
                        <p className="font-semibold text-slate-800">{acc.username}</p>
                        <p className="text-xs text-slate-400">{acc.active ? "Actif" : "Désactivé"}</p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full border font-medium" style={{
                        backgroundColor: { equipe1: "#dbeafe", equipe2: "#e0f2fe", equipe3: "#d1fae5" }[acc.equipe || "equipe1"],
                        color: { equipe1: "#1e40af", equipe2: "#0369a1", equipe3: "#047857" }[acc.equipe || "equipe1"],
                        borderColor: { equipe1: "#93c5fd", equipe2: "#7dd3fc", equipe3: "#6ee7b7" }[acc.equipe || "equipe1"],
                      }}>
                        {{ equipe1: "Equipo 1", equipe2: "Equipo 2", equipe3: "Equipo 3" }[acc.equipe || "equipe1"]}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingPassword === acc.username ? (
                        <div className="flex gap-2 items-center">
                          <Input type="password" value={editPassword} onChange={e => setEditPassword(e.target.value)} placeholder="Nouveau mdp" className="w-36 h-9" data-testid={`edit-password-input-${acc.username}`} />
                          <Button size="sm" onClick={() => changePassword(acc.username)} data-testid={`save-password-${acc.username}`}>OK</Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditingPassword(null); setEditPassword(""); }}>X</Button>
                        </div>
                      ) : (
                        <>
                          <select value={acc.equipe || "equipe1"} onChange={e => updateEquipe(acc.username, e.target.value)} className="text-sm border rounded-lg px-2 py-1.5 bg-white" data-testid={`change-equipe-${acc.username}`}>
                            <option value="equipe1">Equipo 1</option>
                            <option value="equipe2">Equipo 2</option>
                            <option value="equipe3">Equipo 3</option>
                          </select>
                          <Button size="sm" variant="outline" onClick={() => setEditingPassword(acc.username)} data-testid={`change-password-${acc.username}`}>
                            <Edit size={14} className="mr-1" /> Mdp
                          </Button>
                          <Button size="sm" variant={acc.active ? "destructive" : "default"} onClick={() => toggleActive(acc.username, acc.active)} data-testid={`toggle-active-${acc.username}`}>
                            {acc.active ? <><XCircle size={14} className="mr-1" /> Désactiver</> : <><CheckCircle size={14} className="mr-1" /> Activer</>}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deleteTech(acc.username)} data-testid={`delete-tech-${acc.username}`}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TechAdmin;
